import {useMemo, useState} from "react";
import {TransportLineItemEntity} from "@ots-frontend/ots-data";
import {
  DataTableFilterConfig,
  useDataTableFilter,
  UseDataTableFilterProps,
  UseDataTableFilterResult
} from "../DataTable/useDataTableFilter";
import {useTranslation} from "react-i18next";
import {useDebouncedValue} from "@mantine/hooks";

export type UseTransportLineItemDataTableFilterProps = Omit<UseDataTableFilterProps<TransportLineItemEntity>, 'filterConfig'>

export function useTransportLineItemDataTableFilter(props: UseTransportLineItemDataTableFilterProps): UseDataTableFilterResult<TransportLineItemEntity> {
  const {records} = props;

  const {t} = useTranslation(["common"])

  const [resourceIdQuery, setResourceIdQuery] = useState<string | undefined>(undefined);
  const [resourceIdDebouncedQuery] = useDebouncedValue(resourceIdQuery, 200);

  const [ticketNumberQuery, setTicketNumberQuery] = useState<string | undefined>(undefined);
  const [ticketNumberDebouncedQuery] = useDebouncedValue(ticketNumberQuery, 200);

  const [sortField1Query, setSortField1Query] = useState<string | undefined>(undefined);
  const [sortField1DebouncedQuery] = useDebouncedValue(sortField1Query, 200);

  const [sortField2Query, setSortField2Query] = useState<string | undefined>(undefined);
  const [sortField2DebouncedQuery] = useDebouncedValue(sortField2Query, 200);

  const filterConfig: DataTableFilterConfig<TransportLineItemEntity> = useMemo(() => {
    return {
      "resourceId": {
        setQuery: setResourceIdQuery,
        query: resourceIdQuery,
        debouncedQuery: resourceIdDebouncedQuery,
        filter: (record, query) => !record.resourceId || !record.resourceId.toLowerCase().includes(query.toLowerCase())
      },
      "ticketNumber": {
        setQuery: setTicketNumberQuery,
        query: ticketNumberQuery,
        debouncedQuery: ticketNumberDebouncedQuery,
        filter: (record, query) => !record.ticketNumber || !record.ticketNumber.toLowerCase().includes(query.toLowerCase())
      },
      "sortField1": {
        setQuery: setSortField1Query,
        query: sortField1Query,
        debouncedQuery: sortField1DebouncedQuery,
        filter: (record, query) => !record.sortField1 || !record.sortField1.toLowerCase().includes(query.toLowerCase())
      },
      "sortField2": {
        setQuery: setSortField2Query,
        query: sortField2Query,
        debouncedQuery: sortField2DebouncedQuery,
        filter: (record, query) => !record.sortField2 || !record.sortField2.toLowerCase().includes(query.toLowerCase())
      },
    }
  }, [
    resourceIdQuery,
    resourceIdDebouncedQuery,
    ticketNumberQuery,
    ticketNumberDebouncedQuery,
    sortField1Query,
    sortField1DebouncedQuery,
    sortField2Query,
    sortField2DebouncedQuery,
    t
  ])

  const {filteredRecords} = useDataTableFilter({
    records: records,
    filterConfig: filterConfig,
  })

  return {
    filteredRecords: filteredRecords,
    filterConfig: filterConfig
  };
}
